import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  EditOutlined,
  LoginOutlined,
  MailOutlined,
  PhoneOutlined,
  PrinterOutlined,
  QrcodeOutlined,
  SearchOutlined,
  TeamOutlined,
  UndoOutlined,
  UserAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { InputRef } from 'antd';
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  message,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NewGuestModal, {
  type NewGuestAction,
  type NewGuestDraft,
  type SimilarGuest,
} from '../components/NewGuestModal';
import { PrintAgentsModal } from '../components/PrintAgentsModal';
import { PrinterIndicator } from '../components/PrinterIndicator';
import ScanResultModal, {
  type ScanAction,
  type ScanModalState,
} from '../components/ScanResultModal';
import api from '../config/api';
import { badgeCompanyCandidates, badgeCompanyName } from '../config/badgeCompany';
import { badgeName, joinName } from '../config/badgeName';
import type { BadgeContent } from '../config/badgePrint';
import { operatorMovedOn } from '../config/deskFocus';
import { fetchAllTickets } from '../config/fetchAllTickets';
import { LabelPrintError, labelPrintErrorKey } from '../config/labelPrinter';
import { hasChanges, type ScanEdits, scanEditChanges } from '../config/scanEdits';
import { codeStartIn, looksLikeCode, NOT_A_CODE, resolveScan } from '../config/scanLookup';
import { findSimilarNames } from '../config/similarNames';
import { useLabelPrinter } from '../hooks/useLabelPrinter';
import { useSilentRefresh } from '../hooks/useSilentRefresh';
import {
  ATTENDEE_TYPE_LABEL_KEYS,
  ATTENDEE_TYPES,
  attendeeTypeLabelKey,
} from '../i18n/attendeeType';
import { localeTag } from '../i18n/locale';
import { useAuthStore } from '../store/authStore';
import { formatPercent } from '../utils/formatPercent';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const CHECKIN_REFRESH_MS = 15_000;
/** A hung background request would otherwise hold off every later cycle. */
const REFRESH_TIMEOUT_MS = 10_000;

interface Ticket {
  id: string;
  ticketNumber: string;
  status: string;
  purchasedAt: string;
  checkedIn: boolean;
  checkedInAt?: string;
  // #20 / #168 — KI léptette be. A pult kezelőjének neve, vagy az ajtó
  // felirata; a beléptetés visszavonásakor a szerver törli.
  checkedInBy?: string | null;
  /**
   * A #167 beírt „szervezet" mezője — a névtábla cégsorának első lépcsője.
   *
   * A `badgeCompanyName` ezt olvassa elsőként, tehát e mező nélkül a pultnál
   * beírt cégnév elmentődne, de a matricán nem jelenne meg.
   */
  organizationName?: string | null;
  // Direct attendee data (for guest tickets)
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  /**
   * A jegytípus — NAGY T-vel, mert a `GET /tickets` a Prisma-relációt
   * átnevezés nélkül adja (`include: { TicketType: true }`). Mérve valódi
   * backend-válaszon, 2026-09-18. A lap korábban `ticketType`-ot olvasott,
   * ezért a „Jegytípus" oszlop és az ablak jegytípus-sora mindig üres volt.
   *
   * `user` mező NINCS: a `Ticket`-nek nincs `User` relációja, és a sorban sem
   * jön ilyen kulcs (ugyanaz a mérés). Az erre épített ágakat eltávolítottuk —
   * a résztvevő neve, e-mailje és telefonja a jegy saját mezőiből jön.
   */
  TicketType?: {
    id: string;
    name: string;
    price: number;
  };
  // #168 — a cégnév két forrása. A `GET /tickets` a #116 óta MINDKETTŐT
  // kiadja (`tickets.service.ts` `include`-ja), és a szerveroldali kereső is
  // mindkettőre illeszt — ez a képernyő dobta el őket, mert a saját típusa nem
  // ismerte a mezőket. A pultnál viszont a cég az, amiről a résztvevőt
  // megtalálják, és ami a névtábla felső sorára kerül.
  Company?: { name?: string | null } | null;
  Registration?: { companyName?: string | null } | null;
  // #168 / Karlo terve 2026-09-16 — a résztvevő KATEGÓRIÁJA (#165), nem a
  // `TicketType` árazott terméke. A `GET /tickets` `include`-ja a jegy minden
  // skalár mezőjét kiadja, tehát ez a drótról érkezik; a képernyő eddig csak
  // nem nevezte meg. A pulton két helyre kell: a Típus oszlopba és a NÉVTÁBLA
  // harmadik sorára.
  attendeeType?: string;
}

interface Event {
  id: string;
  title: string;
  organizationId: string;
  maxAttendees?: number;
  language?: string;
}

interface Statistics {
  total: number;
  checkedIn: number;
  notCheckedIn: number;
  checkInRate: number;
}

/**
 * A helyszínen felvitt vendég KÉRÉSTÖRZSE.
 *
 * MIÉRT MODULSZINTŰ, TISZTA FÜGGVÉNY. Ugyanaz a végpont és ugyanazok a KÖZÖS
 * mezők, mint a Jegyek oldal „Új jegy" gombjánál — két eltérő boríték ugyanarra
 * a végpontra azt jelentené, hogy a pulton kiadott jegy más adatokkal születik,
 * mint az irodában kiadott, és a különbség csak az esemény után derülne ki.
 *
 * A pult HÁRMAT szándékosan nem tölt: a `companyId`-t (a helyszíni vendéget nem
 * kötjük céges sorhoz, csak a szabad szöveges `organizationName`-t írjuk), a
 * `phone`-t és a `jobTitle`-t (a pultnál nem kérdezzük el). Korábban ez a
 * komment „betűre" azonosat állított — az túlzás volt.
 */
/**
 * A már regisztrált, HASONLÓ NEVŰEK a betöltött listából (§4).
 *
 * Modulszintű, és KÉSZ jelöltlistát kap, nem nyers jegyeket: a lista
 * származtatása a komponensben `useMemo` mögött ül, hogy a gépelés ne
 * normalizálja újra minden leütésre az egész eseményt.
 *
 * The candidates come from the desk list's `attendeeName` (given name first),
 * not from the badge's language-dependent order: the warning compares the
 * name the operator sees in the list.
 */
function newGuestSimilar(draft: NewGuestDraft, candidates: SimilarGuest[]): SimilarGuest[] {
  return findSimilarNames(joinName(draft.firstName, draft.lastName), candidates);
}

function newGuestPayload(draft: NewGuestDraft, eventId: string) {
  return {
    ticketTypeId: draft.ticketTypeId,
    eventId,
    // `undefined`, nem `null`: a DTO `@IsOptional()`-je a HIÁNYZÓ mezőt engedi
    // át, egy explicit `null` viszont az `@IsEmail()`-be futna, és a kérés
    // EGÉSZE 400-at kapna — cím nélküli vendégnél mindig.
    email: draft.email.trim() || undefined,
    firstName: draft.firstName.trim(),
    lastName: draft.lastName.trim(),
    attendeeType: draft.attendeeType,
    // A névtáblára kiírt szervezet (#167). Ez a mező a cégsor ELSŐ lépcsője a
    // `badgeCompanyName` sorrendjében, tehát amit a kezelő beírt, az kerül a
    // matricára is.
    organizationName: draft.company.trim() || undefined,
  };
}

/**
 * A pult keresőmezőjének illesztése egyetlen jegyre.
 *
 * MIÉRT MODULSZINTŰ. A `CheckIn` komponens kognitív komplexitása a §4 ablakával
 * a Biome 15-ös határa fölé nőtt, és a fájl saját előzménye szerint ilyenkor a
 * helyes lépés a kiemelés, nem a szabály kikapcsolása (így született a
 * `ScanResultModal` is). Ez a predikátum semmit nem olvas a komponens
 * állapotából a két argumentumán kívül, tehát a kiemelés semmit nem mozdít a
 * viselkedésén — a `CheckIn.search.test.tsx` a képernyőn keresztül méri.
 */
function matchesTicketSearch(ticket: Ticket, searchText: string): boolean {
  if (searchText === '') return true;
  const searchLower = searchText.toLowerCase();
  // `Boolean(...)`: az opcionális láncok `undefined`-ot adnak vissza, nem
  // `false`-ot. A hamisság ugyanaz, a TÍPUS viszont nem — és a szűrő
  // visszatérési típusa az, ami a hívónál olvasható marad.
  return Boolean(
    ticket.ticketNumber.toLowerCase().includes(searchLower) ||
      ticket.email?.toLowerCase().includes(searchLower) ||
      ticket.firstName?.toLowerCase().includes(searchLower) ||
      ticket.lastName?.toLowerCase().includes(searchLower) ||
      // #168 — a pultnál a cég az, amiről a résztvevőt megtalálják: a delegált
      // a cége nevét mondja, nem a jegyszámát.
      //
      // MINDKÉT forrásra illeszt (`badgeCompanyCandidates`), nem csak arra,
      // amelyik a Cég oszlopba kiíródik. Ha a jegyhez tartozó `Company.name`
      // „Hegyi Kft", a jelentkezéskor beírt cégnév pedig „Studio Present", a
      // delegált a másodikat fogja mondani — és a győztesre szűkített kereső
      // nulla találatot adna rá. Ugyanaz a hiba osztálya, amit a #116 a Jegyek
      // oldalon javított, ott szerveroldali `where.OR`-ral.
      badgeCompanyCandidates(ticket).some((name) => name.toLowerCase().includes(searchLower))
  );
}

/**
 * The warning when a new guest's ticket was created but could not be checked in. A desk without a
 * printer (#202) printed nothing, so its text must not say that a badge did or did not go out.
 */
function newGuestNoCheckInKey(badgePrinting: boolean, printed: boolean) {
  if (!badgePrinting) return 'checkin.newGuestNoCheckInNoPrint';
  return printed ? 'checkin.newGuestNoCheckIn' : 'checkin.newGuestNoPrintNoCheckIn';
}

/**
 * The attendee's name in the desk list, always given name first. The badge
 * uses `badgeName` instead, which follows the event's language.
 *
 * Returns an empty string when there is no name; the "Guest" fallback belongs
 * to the list column only.
 */
function attendeeName(ticket: Ticket): string {
  return `${ticket.firstName || ''} ${ticket.lastName || ''}`.trim();
}

/**
 * A pult felugró ablakának állapota (#168, Karlo terve 2026-09-16).
 *
 * Az alakot az ablak komponense hordozza; itt csak a jegy típusával kötjük
 * össze, hogy a képernyő és az ablak ugyanazt a négy ágat lássa.
 */
type ScanState = ScanModalState<Ticket>;

const CheckIn: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  // Separate from `loading`, which belongs to the ticket table. The cards need
  // their own flag so they can hold their place with a skeleton while the
  // numbers are on their way, instead of vanishing and letting the table jump
  // up under the hand of whoever is tapping through a queue.
  const [statsLoading, setStatsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<string | undefined>();
  const eventLanguage = events.find((event) => event.id === selectedEvent)?.language;
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [filterCheckedIn, setFilterCheckedIn] = useState<boolean | undefined>();
  const [searchText, setSearchText] = useState('');
  // #168 — melyik soron fut ÉPPEN egy nyomtatás. Egyetlen, sorazonosítóval
  // címkézett hely, nem soronkénti halmaz: a `window.print()` a böngészőben
  // modális és blokkoló, tehát egy gépen két nyomtatás nem futhat egyszerre.
  const [printingId, setPrintingId] = useState<string | null>(null);
  /*
    #202 — prints this event's desk badges at all? Read from the event's own
    config (`ticketing.badgePrintingAtCheckin`), tagged with the event it
    belongs to. When it cannot be loaded, the desk only checks in: checking in
    is what every event needs.

    Until the answer for the selected event is in (`deskKnown`), the desk lets
    nobody in. The request runs next to the ticket list, and the rows can
    arrive first: on an event that prints, a row pressed or a code scanned in
    that moment would otherwise check the guest in WITHOUT a badge, under the
    usual success message.
  */
  const [deskConfig, setDeskConfig] = useState<{ eventId: string; badgePrinting: boolean } | null>(
    null
  );
  const deskKnown = deskConfig !== null && deskConfig.eventId === selectedEvent;
  const badgePrinting = deskKnown && deskConfig.badgePrinting;
  // #178 — a Zebra, ha van online print agent, különben a böngésző. A választás
  // nyomtatáskor dől el, a pultosnak nincs mit átkapcsolnia.
  //
  // #202 — it stops only once the event is KNOWN not to print. Started only
  // after the config arrived, the agents would load after the list did, and a
  // row pressed in between would get "the printer is not ready" on an event
  // that prints.
  const knownNotPrinting = deskKnown && !deskConfig.badgePrinting;
  const printer = useLabelPrinter(knownNotPrinting ? undefined : selectedEvent);
  const [printSetupOpen, setPrintSetupOpen] = useState(false);
  // Karlo terve 2026-09-16 — kategória-szűrő a kereső mellett. Kliensoldali,
  // mint a kereső: a lista a #164 óta teljes, tehát a szűrés nem hálózati
  // kérdés. Szerveroldalra tolva minden kategóriaváltás új kérés lenne a
  // pultnál, miközben az adat már a gépen van.
  const [typeFilter, setTypeFilter] = useState<string | undefined>();
  // A beolvasott jegy felugró ablaka. `null` = nincs nyitva.
  const [scanned, setScanned] = useState<ScanState | null>(null);
  /*
    A MOST mutatott ablak, a nyomtatás VÉGÉNEK. A nyomtatás aszinkron: ha
    közben új vendéget olvastak be a nyitott ablakba (#168, 2026-09-18), a
    régi nyomtatás befejezése nem zárhatja be az ÚJ vendég ablakát. A
    lezáró ág ezt olvassa, nem a nyomtatás indulásakori állapotot.
  */
  const scannedRef = useRef<ScanState | null>(null);
  useEffect(() => {
    scannedRef.current = scanned;
  }, [scanned]);
  /**
   * Melyik eseményhez tartozik a képernyőn lévő lista, és sikerült-e betölteni.
   *
   * MIÉRT NEM A `loading` DÖNTI EL. A `loading` azt mondja meg, fut-e ÉPPEN egy
   * kérés — nem azt, hogy amit látunk, ehhez az eseményhez tartozik-e. A kettő
   * eltér: a `setSelectedEvent` és az őt követő effekt lefutása között van egy
   * render, amiben a `loading` még hamis, a lista viszont már az ELŐZŐ eseményé.
   *
   * MIÉRT HÁROM ÁLLAPOT, NEM KETTŐ. Ha csak „kész / nem kész" lenne, egy
   * elbukott betöltés után a lista SOHA nem válna késszé, és a sorba állított
   * beolvasás örökre a várakozó ablakban maradna. Az `ok: false` az, ami a sort
   * le tudja zárni egy őszinte válasszal.
   */
  const [listStatus, setListStatus] = useState<{ eventId: string; ok: boolean } | null>(null);
  /**
   * A beolvasott kód, amíg a lista meg nem érkezik — AHHOZ AZ ESEMÉNYHEZ KÖTVE,
   * amelyik alatt beolvasták. A kötés nélkül a sor maga hozná vissza később a
   * kereszt-eseményes tévedést, amit bezárni akarunk: a kezelő beolvas, eseményt
   * vált, és a kód a MÁSIK esemény listáján oldódna fel.
   */
  const [pendingScan, setPendingScan] = useState<{ code: string; eventId: string } | null>(null);
  const [scanBusy, setScanBusy] = useState<ScanAction | null>(null);
  // A fejléc QR-jelzője. Azt mutatja, amit Karlo terve „kész"-nek nevez: a
  // keresőmező fókuszban van, tehát a billentyűzetként gépelő USB-s olvasó
  // beolvasása ide fog befutni. Ha nincs fókuszban, a beolvasás a semmibe
  // menne — a pultnál ez a leggyakoribb néma hiba.
  const [searchFocused, setSearchFocused] = useState(false);
  /**
   * Az „Új vendég" ablak és a hozzá tartozó jegytípusok (Karlo §4).
   *
   * A jegytípus a kiadás KÖTELEZŐ mezője (`CreateTicketBaseDto.ticketTypeId`),
   * és nincs benne a jegylistában — a sorokon a jegyhez TARTOZÓ típus jön, nem
   * az esemény kínálata. Ezért külön kérés, eseményváltáskor.
   */
  const [ticketTypes, setTicketTypes] = useState<Array<{ id: string; name: string }> | null>([]);
  const [newGuestOpen, setNewGuestOpen] = useState(false);
  /** Melyik gomb dolgozik az ablakban; `null`, ha egyik sem. */
  const [newGuestSaving, setNewGuestSaving] = useState<NewGuestAction | null>(null);
  const searchRef = useRef<InputRef>(null);
  const { user } = useAuthStore();

  useEffect(() => {
    fetchEvents();
  }, []);

  // Which event the screen is on right now, readable from inside a fetch after
  // its `await`. Neither fetch below can use `selectedEvent` for that: each call
  // closes over the value from the render that started it, so a request fired
  // for a previous event cannot tell that the operator has moved on — and both
  // its success and its failure would write over the current event's data.
  // Measured, on the very build that fixed the failure path: a 200 arriving
  // seconds after the switch put the PREVIOUS event's totals back on screen,
  // above the new event's single unchecked ticket, with no message at all.
  const liveEventRef = useRef<string | undefined>(undefined);
  // Request order: a background refresh already on its way must not put back a
  // list a check-in has since changed.
  const ticketsSeq = useRef(0);
  const statsSeq = useRef(0);
  const writesInFlight = useRef(0);
  const refreshInFlight = useRef(false);

  useEffect(() => {
    liveEventRef.current = selectedEvent;
    if (selectedEvent) {
      // Cleared before the request goes out, not after it comes back. What is
      // on screen belongs to the event being left, so it is not one refresh
      // behind — it is another event's. On a venue connection "until the
      // response arrives" is seconds of a door screen showing a check-in rate
      // that was never this event's.
      setStatistics(null);
      // Ugyanaz a csapda, mint a statisztikánál: ami a képernyőn van, az az
      // ELHAGYOTT eseményé. Enélkül a váltás után azonnal megnyitott ablak az
      // ELŐZŐ esemény jegytípusát kínálná — egyetlen típus mellett magától
      // kiválasztva —, a szerver pedig nyers, angol 400-zal utasítaná el a
      // pultnál. Mérve: az átnézés ezt találta meg.
      setTicketTypes([]);
      fetchTickets(selectedEvent);
      fetchStatistics(selectedEvent);
      fetchTicketTypes(selectedEvent);
      fetchDeskConfig(selectedEvent);
    }
  }, [selectedEvent]);

  const fetchDeskConfig = async (eventId: string) => {
    try {
      const response = await api.get(`/events/${eventId}/config`, { silentError: true } as never);
      if (eventId !== liveEventRef.current) return;
      setDeskConfig({
        eventId,
        badgePrinting: response.data?.ticketing?.badgePrintingAtCheckin === true,
      });
    } catch (error) {
      if (eventId !== liveEventRef.current) return;
      console.error('Desk settings load failed:', error);
      setDeskConfig({ eventId, badgePrinting: false });
      message.error(t('checkin.deskSettingsLoadFailed'));
    }
  };

  const fetchEvents = async () => {
    try {
      const params: any = {};
      if (user?.organizationId) {
        params.organizationId = user.organizationId;
      }
      const response = await api.get('/events', { params });
      setEvents(response.data.data || []);
    } catch (error) {
      message.error(t('checkin.eventsLoadFailed'));
    }
  };

  const fetchTickets = async (eventId: string | undefined) => {
    if (!eventId) return;

    const seq = ++ticketsSeq.current;
    setLoading(true);
    try {
      // #164 — the whole list, not the server's default first 20.
      //
      // This screen filters client-side over whatever is in `tickets`
      // (`filteredTickets` below), so a short list is not a short list at the
      // door: it is an attendee the operator cannot find at all. With 176
      // tickets on Connect+Grow 2026, 156 of them were unreachable.
      //
      // The staleness guard moved INTO the helper, and now runs after every
      // page rather than once at the end. Paging made that necessary: a fetch
      // for an event we have left would otherwise keep requesting its
      // remaining pages and then write them over the event we are on.
      const result = await fetchAllTickets<Ticket>(
        { eventId },
        { isStale: () => eventId !== liveEventRef.current }
      );
      if (!result) return;
      setTickets(result.data);
      setListStatus({ eventId, ok: true });
    } catch (error) {
      if (eventId !== liveEventRef.current) return;
      message.error(t('checkin.ticketsLoadFailed'));
      setTickets([]);
      // Lezárt, de SIKERTELEN. Enélkül a sorba állított beolvasás örökre
      // várakozna, a kezelő pedig egy pörgő ablakot bámulna a pultnál.
      setListStatus({ eventId, ok: false });
    } finally {
      // By request order, not by event: a late request for an event already
      // left (a check-in finishing after a switch) must still clear its spinner.
      if (seq === ticketsSeq.current) setLoading(false);
    }
  };

  /**
   * Az esemény jegytípusai — kizárólag az „Új vendég" kiadásához (#168, §4).
   *
   * HÁROM ÁLLAPOT, NEM KETTŐ. A jegytípus a kiadás kötelező mezője, tehát
   * nélküle a szerver 400-zal állna meg — ezt az ablak előre kiírja. Csakhogy
   * az ÜRES lista és az ELHASALT kérés nem ugyanaz: az elsőre a helyes teendő
   * jegytípust létrehozni az esemény beállításainál, a másodikra újrapróbálni.
   * Egyetlen üres tömbbel a kezelőt egy ép beállítást „javítani" küldenénk,
   * hálózati hiba után. Ezért a bukás `null`.
   *
   * A `silentError` a globális toastot veszi le: ez a kérés a képernyő
   * mellékszála, nem szabad piros sávval bejelentkeznie egy sorban álló
   * résztvevő fölött — az üzenetet az ablak mondja el, a helyén.
   */
  const fetchTicketTypes = async (eventId: string | undefined) => {
    if (!eventId) return;
    try {
      const response = await api.get(`/tickets/types/event/${eventId}`, {
        silentError: true,
      } as never);
      // Eseményváltás közben beérkező válasz: a MÁSIK esemény jegytípusai
      // kerülnének az ablakba, és a kiadás idegen esemény típusára hivatkozna.
      if (eventId !== liveEventRef.current) return;
      setTicketTypes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (eventId !== liveEventRef.current) return;
      console.error('Ticket types load failed:', error);
      setTicketTypes(null);
    }
  };

  const fetchStatistics = async (eventId: string | undefined) => {
    if (!eventId) return;

    const seq = ++statsSeq.current;
    setStatsLoading(true);
    try {
      const response = await api.get(`/tickets/statistics/${eventId}`);
      if (eventId !== liveEventRef.current) return;
      setStatistics(response.data);
    } catch (error) {
      // Developer log line, deliberately not translated: a message that changes
      // language with the interface cannot be searched for.
      console.error('Failed to load check-in statistics:', error);
      // A failure for an event we have left says nothing about the one we are
      // on: without this line, a late error for the previous event wiped the
      // current event's CORRECT numbers and complained about a selection the
      // operator could no longer see.
      if (eventId !== liveEventRef.current) return;
      // Clearing the state is the part that matters, and it has to happen even
      // though nothing here is rendered from `error`. The cards sit behind
      // `statistics`, so a stale object survives an event switch: the table
      // below correctly shows the new event's tickets — or "no tickets" —
      // while the four cards still count the previous one, with nothing on the
      // page saying so. That is a check-in rate an organizer acts on. Absent
      // numbers cannot mislead anybody; wrong ones can, and this screen is read
      // at a door while people are queueing.
      setStatistics(null);
      message.error(t('checkin.statisticsLoadFailed'));
    } finally {
      if (seq === statsSeq.current) setStatsLoading(false);
    }
  };

  /**
   * A backend „már be van jelentkeztetve" elutasítása (#168).
   *
   * MIÉRT KELL MEGKÜLÖNBÖZTETNI a többi hibától: ez nem hiba, hanem verseny.
   * A pultnál KÉT gép dolgozik ugyanazon a listán, és ez a képernyő csak
   * 15 másodpercenként kérdez rá. Tehát ha az A gép beengedi a résztvevőt, a
   * B gép sorában a következő frissítésig a
   * `checkedIn` még hamis marad. A B gépen a Nyomtatás gomb ilyenkor kiadja a
   * címkét, majd beléptetni próbál — és 400-at kap.
   *
   * A kliensoldali `record.checkedIn` őr ezt NEM tudja megfogni: friss listát
   * feltételez. A szerver válasza az egyetlen forrás, ami tudja az igazságot.
   */
  const isAlreadyCheckedIn = (error: unknown): boolean => {
    const body = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
    return typeof body === 'string' && /already checked in/i.test(body);
  };

  type CheckInOutcome = 'checked-in' | 'already-checked-in' | 'failed';

  /**
   * A beléptetés EGYETLEN írója ezen a képernyőn.
   *
   * Nem ír üzenetet: a hívó dönti el, mit jelent az eredmény nála. A
   * Bejelentkeztetés gombnak a „már bent van" valódi információ; a Nyomtatás
   * gombnak viszont csak annyit jelent, hogy újranyomtatás történt.
   */
  const checkInTicket = async (ticketId: string): Promise<CheckInOutcome> => {
    writesInFlight.current += 1;
    try {
      // `silentError`: az api-kliens globális interceptora minden elhasalt
      // kérésre kiír egy toastot a szerver NYERS, angol üzenetével — az én
      // `catch`-em ELŐTT (`config/api.ts`). Enélkül a pultnál a „már bent van"
      // esetben a kezelő EGYSZERRE kapná a piros „Ticket already checked in"
      // sort és a zöld „Névtábla újranyomtatva"-t. Valódi böngészőben mérve,
      // mindkettő megjelent.
      //
      // Ez a függvény maga írja az üzeneteit, tehát a kapcsoló idetartozik.
      // Mellékhatásként a külön Bejelentkeztetés gomb KETTŐS toastja is
      // megszűnik — az a bázison is megvolt, mert a kezelő és az interceptor
      // ugyanazt a szöveget írta ki kétszer.
      //
      // Az `as never` a panel bevált alakja erre (`Sponsors.tsx:547`): a
      // kapcsolót az `AxiosRequestConfig` típusa nem ismeri, csak a saját
      // interceptorunk olvassa. Nem nyitok rá új alakot.
      await api.post(`/tickets/${ticketId}/check-in`, undefined, {
        silentError: true,
      } as never);
      fetchTickets(selectedEvent);
      fetchStatistics(selectedEvent);
      return 'checked-in';
    } catch (error) {
      if (isAlreadyCheckedIn(error)) {
        // A lista elavult — frissítjük, hogy a képernyő utolérje a másik gépet.
        fetchTickets(selectedEvent);
        fetchStatistics(selectedEvent);
        return 'already-checked-in';
      }
      message.error(
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          t('checkin.checkInFailed')
      );
      return 'failed';
    } finally {
      writesInFlight.current -= 1;
    }
  };

  /**
   * A pult gombja: névtábla nyomtatása, majd érkeztetés (#168).
   *
   * KÉT SZABÁLY, amit ez a sorrend tart be:
   *
   * 1. **A második megnyomás nem hibázhat.** Ezt KÉT őr tartja, nem egy:
   *    - a soron álló `checkedIn`, ami a saját gép beléptetését fogja meg, és
   *    - a szerver „already checked in" válasza, ami a MÁSIK pultgépét.
   *    Az első egymagában kevés: friss listát feltételez, a képernyő viszont
   *    csak 15 másodpercenként frissül. Két gép mellett a kezelő piros angol
   *    hibaüzenetet kapott volna egy sikeresen kinyomtatott címke után.
   *
   * 2. **Sikertelen nyomtatás nem jelöl érkezettnek.** Ami a képernyőn
   *    elhasalt, arról nem írunk állapotot: a kezelő úgyis újra megnyomja, és
   *    akkor mindkettő megtörténik. Fordítva egy néma érkeztetés maradna egy
   *    ki nem nyomtatott névtábla mögött.
   *
   * 3. **A sor a TELJES művelet végéig pörög.** A `printingId` csak azután
   *    tisztul, hogy a beléptetés is lefutott. Ha a nyomtatás után azonnal
   *    felszabadulna, a gomb a beléptető kérés teljes körideje alatt újra
   *    nyomható lenne — ugyanarra a sorra, még mielőtt a lista frissül.
   *
   * Az érkeztetés a PANEL útvonalán megy (`checkInTicket`), nem a Jegyek oldal
   * „Érvényesítés" gombjáén: azok külön írók, és a rossz választása után a
   * pult és az ajtó más számot mutatna.
   */
  /**
   * A kategóriasor szövege a névtáblára és a Típus oszlopba (#165 szótára).
   *
   * Ismeretlen értékre ÜRES sztringet ad, nem a nyers enum-értéket. A
   * statisztika-csempe a nyerset írja ki, és ott ez helyes — egy szám mellett
   * az őszinte „SPONSOR" jobb, mint a semmi. A NÉVTÁBLÁN nem: ott a résztvevő
   * mellkasára kerülne ki egy angol adatbázis-érték.
   */
  const attendeeTypeLabel = (record: Ticket): string => {
    const key = record.attendeeType ? attendeeTypeLabelKey(record.attendeeType) : null;
    return key ? t(key) : '';
  };

  /**
   * MI KERÜL A CÍMKÉRE — egyetlen helyen.
   *
   * Három hívója van: a sor Nyomtatás gombja, a felugró ablak gombja és az
   * ablak ÉLŐ ELŐNÉZETE. Ha az előnézet külön állítaná össze a tartalmat, a
   * kezelő mást látna a képernyőn, mint amit a papír kap — és pont az
   * előnézet dolga lenne ezt kizárni.
   */
  const badgeContent = (record: Ticket, edits?: ScanEdits): BadgeContent => {
    // A pult ablakában a kezelő JAVÍTHATJA a cégnevet és a nevet (Karlo §2).
    // Ilyenkor a javított érték megy a címkére és az előnézetbe is — a
    // `badgeCompanyName` sorrendje csak a javítás HIÁNYÁBAN dönt.
    // Both branches go through `badgeName`: if the edited name were assembled
    // in another order, editing one field would silently flip the name.
    const source = edits ?? ticketOriginal(record);
    const name = badgeName(source.firstName, source.lastName, eventLanguage);

    return {
      company: edits ? edits.company.trim() : source.company,
      // Név híján a jegyszám megy a címkére. Üres vastag sor a pultnál
      // használhatatlan nyomat: a kezelőnek valamit kézbe kell adnia,
      // amiről a résztvevőt a listán azonosítani tudja.
      name: name || record.ticketNumber,
      type: attendeeTypeLabel(record),
    };
  };

  /**
   * What the badge shows for this ticket before any correction: the resolved company and the name
   * split into its two fields. The window fills its fields from it, Save compares against it and the
   * badge falls back to it, so none of them can disagree on what the unedited badge says.
   *
   * The name comes from the ticket's own fields, not from `attendeeName`: that is one string, and
   * "Jan van den Berg" cannot be split back into two fields.
   */
  const ticketOriginal = (record: Ticket): ScanEdits => ({
    company: badgeCompanyName(record),
    lastName: record.lastName || '',
    firstName: record.firstName || '',
  });

  /**
   * A nyomtatási hiba a kezelő nyelvén (#178). A Zebra útja konkrét okot tud
   * (a nyomtató hibát jelzett / senki nem vette át / nem jött visszaigazolás);
   * a böngészősé csak annyit, hogy nem indult el.
   */
  const printErrorText = (
    error: unknown,
    fallbackKey: 'checkin.printFailed' | 'checkin.calibrationPrintFailed' = 'checkin.printFailed'
  ): string => {
    const key = labelPrintErrorKey(error);
    if (!key) return t(fallbackKey);
    const detail = error instanceof LabelPrintError ? error.printerMessage : undefined;
    return t(key, { error: detail || t('checkin.printerUnknownError') });
  };

  const handlePrintBadge = async (record: Ticket, edits?: ScanEdits) => {
    setPrintingId(record.id);
    try {
      try {
        await printer.printBadge(badgeContent(record, edits), record.id);
      } catch (error) {
        // A kezelő a saját nyelvén kapja a hibát. A `printBadge` saját
        // szövege angol és fejlesztőnek szól — az a naplóba megy.
        if (error instanceof Error) console.error('Badge printing failed:', error);
        message.error(printErrorText(error));
        return;
      }

      if (record.checkedIn) {
        message.success(t('checkin.reprintSuccess'));
        return;
      }

      const outcome = await checkInTicket(record.id);
      if (outcome === 'checked-in') {
        message.success(t('checkin.checkInSuccess'));
      } else if (outcome === 'already-checked-in') {
        // A másik pultgép már beengedte. A címke kiment, az érkezés rögzítve —
        // a kezelőnek ez újranyomtatás, nem hiba.
        message.success(t('checkin.reprintSuccess'));
      }
    } finally {
      setPrintingId(null);
    }
  };

  /**
   * Checking in on a desk that does not print (#202). Same writer and the same
   * busy row as the print path, so the row spins until the check-in is done
   * and cannot be pressed twice in the meantime.
   */
  const handleCheckInOnly = async (record: Ticket) => {
    setPrintingId(record.id);
    try {
      const outcome = await checkInTicket(record.id);
      if (outcome === 'checked-in') {
        message.success(t('checkin.checkInSuccess'));
      } else if (outcome === 'already-checked-in') {
        // Another desk let them in first. Without a badge to hand over there
        // is nothing left to do, but the operator should know why nothing
        // changed.
        message.info(t('checkin.checkInElsewhere'));
      }
    } finally {
      setPrintingId(null);
    }
  };

  /** The desk's primary action: print and check in, or only check in where the event has no printer. */
  const handleAdmit = async (record: Ticket, edits?: ScanEdits) => {
    // Not known yet whether this desk prints: see `deskKnown`. The row shows
    // a spinner meanwhile, so nothing happening here is not a silent drop.
    if (!deskKnown) return;
    await (badgePrinting ? handlePrintBadge(record, edits) : handleCheckInOnly(record));
  };

  /**
   * A fókusz visszaadása a keresőmezőnek.
   *
   * Karlo folyamatábrájának 4. lépése: „Az ablak bezárul, a fókusz visszakerül
   * a keresőbe – jöhet a következő vendég." Ez nem kényelmi funkció. Az USB-s
   * olvasó billentyűzetként gépel, tehát fókusz nélkül a KÖVETKEZŐ beolvasás
   * a semmibe megy: a kezelő beolvas, nem történik semmi, és a hibát a
   * vonalkódra vagy az olvasóra fogja.
   */
  const focusSearch = () => {
    // Selected, so the next scan or typed name replaces the leftover search instead of appending to it.
    searchRef.current?.focus({ cursor: 'all' });
  };

  /** The row's own button: once it finishes, the next reader scan must land in the search box. */
  const handleRowPrint = async (record: Ticket) => {
    const searchBefore = searchRef.current?.input?.value;
    try {
      await handleAdmit(record);
    } finally {
      if (
        !operatorMovedOn(document.activeElement, searchRef.current?.input ?? null, searchBefore)
      ) {
        focusSearch();
      }
    }
  };

  /**
   * A feloldás eredményének kiírása az ablakba.
   *
   * KÉT hívója van — az azonnali út és a lista megérkezése utáni feloldás —, és
   * pontosan azért egyetlen függvény, hogy a kettő ne tudjon eltérni: a sorból
   * előkerülő kód ugyanazt az ablakot kapja, mint a rögtön feloldott.
   *
   * `useCallback`, mert az alábbi effekt függ tőle. Enélkül minden renderben új
   * függvény jönne létre, és a hook függőségi listája hazudna — a Biome ezt
   * külön szabállyal fogja meg. A két függősége az, amit a feloldás tényleg
   * olvas.
   */
  const showScanResult = useCallback(
    (value: string) => {
      const result = resolveScan(value, tickets, selectedEvent);
      if (result.kind === NOT_A_CODE) {
        setSearchText(value);
        return;
      }
      // A mező kiürül, hogy a következő beolvasás ne az előző kód mögé gépeljen
      // — és hogy a lista alatta ne maradjon egyetlen sorra szűkítve.
      setSearchText('');
      setScanned(
        result.kind === 'ticket'
          ? { kind: 'ticket', ticket: result.ticket, via: 'scan' }
          : { kind: result.kind, code: value.trim() }
      );
    },
    [tickets, selectedEvent]
  );

  /**
   * A KIVÁLASZTOTT eseményhez tartozó listaállapot, vagy `null`.
   *
   * Kiírva, nem `listStatus?.eventId === selectedEvent` alakban: ha mindkettő
   * `undefined`, az a rövid alak IGAZAT ad — esemény nélkül, betöltés nélkül
   * késznek mondaná a listát.
   */
  const listHere =
    selectedEvent !== undefined && listStatus !== null && listStatus.eventId === selectedEvent
      ? listStatus
      : null;
  /** A lista ehhez az eseményhez tartozik ÉS be is töltött. */
  const listReady = listHere?.ok === true;
  /** A lista ehhez az eseményhez tartozik, de a betöltése ELBUKOTT. */
  const listFailed = listHere?.ok === false;

  /**
   * Enter a keresőmezőben — beolvasás VAGY sima keresés.
   *
   * Ugyanabba a mezőbe gépel az olvasó és a kezelő, ezért a döntést a
   * {@link resolveScan} hozza meg. Névre ütött Enter után nem nyílhat ablak.
   */
  const handleSearchEnter = (value: string) => {
    // Amíg a lista nincs KÉSZ, a feloldás nem dönthet: üres listán a JELEN
    // esemény QR-je is „nincs ilyen jegy"-nek látszana, eseményváltás közben
    // pedig még az előző esemény sorai állnak.
    if (selectedEvent && !listReady && looksLikeCode(value, selectedEvent)) {
      setSearchText('');
      // ELBUKOTT betöltés mellett sorba állni tilos: semmi nem oldaná fel, a
      // kezelő egy örökké pörgő ablakot kapna. A válasz azonnal a megnevezett
      // kudarc — és ez teszi őszintévé az ablak „olvassa be még egyszer"
      // kérését: az újabb beolvasás megint ezt kapja, nem pedig egy hamis
      // „nincs ilyen jegy"-et egy érvényes jegyre.
      if (listFailed) {
        setScanned({ kind: 'list-failed', code: value.trim() });
        return;
      }
      setPendingScan({ code: value, eventId: selectedEvent });
      setScanned({ kind: 'pending', code: value.trim() });
      return;
    }
    showScanResult(value);
  };

  /**
   * A nyitott ablakba olvasott kód (#168, 2026-09-18).
   *
   * Ugyanazon az úton dől el, mint a keresőmezőbe olvasott — a kezelő a
   * következő vendéget olvasta be. EGY kivétellel: ami nem is kódnak látszik,
   * az a keresőmezőben névkeresés lenne, és a HÁTTÉRBEN, az ablak mögött
   * szűrné le a listát, miközben az ablakban semmi nem változik. Itt ezért
   * „Nem található" ablak lesz belőle.
   */
  const handleWindowScan = (value: string) => {
    if (resolveScan(value, tickets, selectedEvent).kind === NOT_A_CODE) {
      setScanned({ kind: 'not-found', code: value.trim() });
      return;
    }
    handleSearchEnter(value);
  };

  const closeScan = () => {
    setScanned(null);
    // A várakozó ablak bezárása a SORT is eldobja. Enélkül a lista
    // megérkezésekor magától felugrana egy ablak, amit a kezelő már elutasított.
    setPendingScan(null);
    focusSearch();
  };

  const closeNewGuest = () => {
    setNewGuestOpen(false);
    focusSearch();
  };

  /**
   * Closes the window only while it still shows this ticket; a scan that arrived during the
   * action opened another guest's window, which stays open.
   */
  const closeScanIfShowing = (ticketId: string) => {
    const shown = scannedRef.current;
    if (shown?.kind === 'ticket' && shown.ticket.id === ticketId) closeScan();
  };

  /**
   * A sorba állított beolvasás feloldása, amint a lista megérkezett.
   *
   * Három kimenet van, és mind a három szándékos:
   *  - betöltött ehhez az eseményhez → normál feloldás, az ablak magától
   *    megtelik, a kezelőnek nem kell újra beolvasnia;
   *  - a betöltés elbukott → MEGNEVEZETT kudarc, nem „nincs ilyen jegy";
   *  - a kezelő közben eseményt váltott → a sor eldobódik, mert a kód a MÁSIK
   *    esemény alatt született, és ezen a listán feloldani hazugság lenne.
   */
  useEffect(() => {
    if (!pendingScan) return;

    if (pendingScan.eventId !== selectedEvent) {
      setPendingScan(null);
      setScanned(null);
      return;
    }
    if (listStatus?.eventId !== pendingScan.eventId) return;

    setPendingScan(null);
    if (listStatus.ok) {
      showScanResult(pendingScan.code);
    } else {
      setScanned({ kind: 'list-failed', code: pendingScan.code.trim() });
    }
  }, [pendingScan, listStatus, selectedEvent, showScanResult]);

  /**
   * Saves the operator's correction to the ticket, sending only what `scanEditChanges` reports as
   * moved. Throws on failure; each caller words its own message.
   */
  const saveScanEdits = async (ticket: Ticket, edits: ScanEdits): Promise<void> => {
    const changes = scanEditChanges(ticketOriginal(ticket), edits);
    if (!hasChanges(changes)) return;

    // `silentError`: a saját üzenetünk megy ki, nem az interceptor nyers,
    // angol szövege — ugyanaz az indok, mint a `checkInTicket`-nél.
    await api.patch(`/tickets/${ticket.id}`, changes, { silentError: true } as never);
  };

  /**
   * A felugró ablak egyetlen elsődleges gombja.
   *
   * Ugyanaz a művelet, mint a soré — szándékosan ugyanazon a `handlePrintBadge`
   * úton, nem egy második írón. Két külön írónál a pult ablakból és sorból
   * indított beléptetése idővel szétcsúszna.
   */
  const handleScanPrint = async (ticket: Ticket, edits: ScanEdits) => {
    // Before anything is saved: the window stays open, and the operator can
    // press again in a moment, once the desk knows whether it prints.
    if (!deskKnown) return;
    setScanBusy('print');
    try {
      // ELŐBB a mentés: ha sikerül, a lista frissítése után a sorban is a
      // javított név áll, és az ablak bezárása nem hagy maga után eltérést a
      // képernyő és a papír között.
      /*
        MIÉRT NEM ÁLLÍTJA MEG A NYOMTATÁST, HA A MENTÉS ELHASAL. A pultnál sor
        áll. A résztvevő szempontjából az a fontos, hogy a HELYES név kerüljön a
        mellkasára; hogy az adatbázis is utolérje, az utólag pótolható. A kezelő
        viszont nem maradhat abban a hitben, hogy elmentődött — ezért kap külön,
        kimondott hibaüzenetet arról, hogy a jegyen a régi adat maradt.
      */
      try {
        await saveScanEdits(ticket, edits);
      } catch (error) {
        console.error('Saving the scanned ticket correction failed:', error);
        message.error(
          t(badgePrinting ? 'checkin.scanSaveFailed' : 'checkin.scanSaveFailedNoPrint')
        );
      }
      await handleAdmit(ticket, edits);
    } finally {
      setScanBusy(null);
      closeScanIfShowing(ticket.id);
    }
  };

  /** The correction alone: no print, no check-in, and on failure the window stays open. */
  const handleScanSave = async (ticket: Ticket, edits: ScanEdits) => {
    setScanBusy('save');
    try {
      await saveScanEdits(ticket, edits);
    } catch (error) {
      console.error('Saving the ticket correction failed:', error);
      message.error(t('checkin.scanSaveOnlyFailed'));
      return;
    } finally {
      setScanBusy(null);
    }
    message.success(t('checkin.scanSaveOnlySuccess'));
    refreshList();
    closeScanIfShowing(ticket.id);
  };

  /**
   * A BEÍRT vendég névtáblája — ugyanaz a három sor, mint a jegyeké.
   *
   * Nem a `badgeContent`-et hívja, mert annak `Ticket` kell, és itt még nincs
   * jegy: az ablak épp azt mutatja meg, mi LENNE a papíron, mielőtt bármi
   * létrejön. A három sor összeállítása viszont betű szerint ugyanaz — a
   * névsorrend is (`badgeName`), különben az ablakban jóváhagyott név
   * máshogy állna össze a nyomaton.
   */
  const newGuestBadge = (draft: NewGuestDraft): BadgeContent => {
    const name = badgeName(draft.firstName, draft.lastName, eventLanguage);
    const key = draft.attendeeType ? attendeeTypeLabelKey(draft.attendeeType) : null;
    return {
      company: draft.company.trim(),
      // Névtelen jegyen a jegyszám a tartalék; itt még nincs jegyszám, tehát a
      // beírás előtti előnézet üres névvel áll. Ez helyes: a gomb úgyis kötelezi
      // a nevet, és egy odaképzelt „TKT-…" hazudna a papírról.
      name,
      type: key ? t(key) : '',
    };
  };

  /**
   * A helyszínen felvitt vendég kiadása (Karlo §4).
   *
   * A BORÍTÉK a Jegyek oldal „Új jegy" gombjáé (`Tickets.tsx`), betűre: ugyanaz
   * a végpont, ugyanazok a mezők. Ez nem másolás kényelemből — két eltérő
   * boríték ugyanarra a végpontra azt jelentené, hogy a pulton kiadott jegy
   * más adatokkal születik, mint az irodában kiadott, és a különbség csak az
   * esemény után derülne ki.
   *
   * AMI SZÁNDÉKOSAN KÜLÖN LÉPÉS: a beléptetés. A végpont fogadna
   * `checkedIn: true`-t is, de akkor a jegyen NEM lenne ott, KI léptette be
   * (`checkedInBy`, #20) — a pult ablaka pedig épp azt a nevet írja ki egy
   * második beolvasáskor. Ezért a jegy kiadása után a képernyő SAJÁT
   * beléptető útja fut (`checkInTicket`), ugyanaz, amit a sor gombja hív.
   */
  const handleNewGuest = async (draft: NewGuestDraft, action: NewGuestAction) => {
    if (!selectedEvent) return;
    // The check-in button waits for the desk setting (`deskKnown`) before the
    // ticket is created, so it cannot be issued and let in without its badge.
    if (action !== 'save' && !deskKnown) return;
    setNewGuestSaving(action);

    /*
      A `try` CSAK a kiadásra szűkül, a nyomtatásra és a beléptetésre NEM.

      A tágabb alak, amit az átnézés megfogott: a `catch` üzenete azt mondja,
      hogy „a jegyet nem sikerült kiadni, az adatok megmaradtak, próbálja
      újra" — és ebből a kiadás UTÁN mind a három állítás hamis. A jegy
      létrejött, az ablak már bezárult, az újrapróbálás pedig MÁSODIK jegyet
      adna ugyanannak a vendégnek: pontosan az a hiba, ami ellen a hasonló-név
      figyelmeztetés van. Ma a mögötte álló segédek elnyelik a saját hibájukat,
      tehát az ág szűk — de ez szerkezeti őr, nem a véletlenen múlik.
    */
    let created: unknown;
    try {
      created = await api.post('/tickets/admin/create', newGuestPayload(draft, selectedEvent));
    } catch (error) {
      // A jegy NEM jött létre. Az ablak NYITVA MARAD a beírt adatokkal: a
      // pultnál egy elhasalt kérés miatt újragépeltetni a nevet és a céget azt
      // jelentené, hogy a hiba a résztvevő idejébe kerül.
      console.error('Guest ticket creation failed:', error);
      message.error(
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          t('checkin.newGuestFailed')
      );
      setNewGuestSaving(null);
      return;
    }

    closeNewGuest();
    try {
      if (action === 'save') {
        refreshList();
        message.success(t('checkin.newGuestSaved'));
        return;
      }
      await printAndCheckInNewGuest(draft, (created as { data?: { id?: string } })?.data?.id);
    } finally {
      setNewGuestSaving(null);
    }
  };

  /**
   * A frissen kiadott jegy névtáblája és beléptetése (§4 második gombja).
   *
   * A LISTAFRISSÍTÉS PONTOSAN EGYSZER fut minden kimeneten. A korábbi alak
   * kiadás után is frissített, majd a `checkInTicket` ismét — két
   * sorrendezetlen kérés ugyanarra az eseményre. A `fetchTickets` őre csak az
   * eseményt nézi, kérés-sorrendet nem, tehát ha a KORÁBBI fut be utoljára, a
   * képernyőn a beléptetés ELŐTTI pillanatkép marad: a frissen beengedett
   * vendég „várakozó"-ként áll a pulton. A képernyő csak 15 másodpercenként frissül, a
   * kezelő természetes válasza pedig az, hogy mégegyszer beléptet vagy újra
   * kiad — vagyis a hazugság jegyet szül.
   */
  const printAndCheckInNewGuest = async (draft: NewGuestDraft, ticketId?: string) => {
    // #202 — without a printer the badge step is skipped and counts as done;
    // only the messages, which would otherwise mention a badge, differ.
    const printed = badgePrinting ? await printNewGuestBadge(draft) : true;
    if (!ticketId) {
      // A jegy létrejött, de a válaszból nem tudjuk, melyik: beléptetni nem
      // tudunk. Hogy a címke KIMENT-e, az külön kérdés — ne mondjuk rá, hogy
      // igen, ha épp az bukott el.
      message.warning(t(newGuestNoCheckInKey(badgePrinting, printed)));
      refreshList();
      return;
    }
    // Sikertelen nyomtatás nem jelöl érkezettnek — ugyanaz a szabály, mint a
    // sor gombjánál. A jegy ettől még létezik, a sorából újra nyomtatható.
    if (!printed) {
      refreshList();
      return;
    }
    // A `checkInTicket` maga frissíti a listát és a számokat, mindkét ágán.
    if ((await checkInTicket(ticketId)) !== 'failed') {
      message.success(
        t(badgePrinting ? 'checkin.newGuestCheckedIn' : 'checkin.newGuestCheckedInNoPrint')
      );
    }
  };

  /** A lista és a négy szám újrakérése — egy helyen, hogy ne fusson kétszer. */
  const refreshList = () => {
    fetchTickets(selectedEvent);
    fetchStatistics(selectedEvent);
  };

  /** A beírt vendég címkéje a nyomtatóra; `false`, ha a nyomtatás elhasalt. */
  const printNewGuestBadge = async (draft: NewGuestDraft): Promise<boolean> => {
    try {
      await printer.printBadge(newGuestBadge(draft));
      return true;
    } catch (error) {
      if (error instanceof Error) console.error('Badge printing failed:', error);
      message.error(printErrorText(error));
      return false;
    }
  };

  /**
   * Tesztcímke a fejlécből (Karlo terve, „Nyomtató" jelző).
   *
   * MIÉRT VAN RÁ SZÜKSÉG. A `window.print()` nem tud a nyomtató állapotáról
   * semmit — nincs mit lekérdezni, ezért a jelző maga semleges. Az egyetlen
   * mérés, amit a kezelő a pult felállításakor el tud végezni, az egy valódi
   * nyomat: jó-e a lapméret, a tájolás, az igazítás. Ez az a gomb.
   */
  const handleTestLabel = async () => {
    try {
      await printer.printTestLabel({
        company: t('checkin.testLabelCompany'),
        name: t('checkin.testLabelName'),
        type: t('checkin.testLabel'),
      });
    } catch (error) {
      if (error instanceof Error) console.error('Badge printing failed:', error);
      message.error(printErrorText(error));
    }
  };

  /**
   * KALIBRÁCIÓS címke a fejlécből (#168, Norbert kérése 2026-09-17).
   *
   * KÉT KÜLÖN GOMB, nem egy. A minta-Tesztcímke azt mutatja meg, MILYEN lesz a
   * névtábla; ez azt, hogy MEKKORA — a nyomtató a névleges 60 × 40 mm-t
   * adja-e ki, vagy skáláz. Egy lapon a kettő elrontaná egymást: a
   * kalibrációs keretnek a lap SZÉLÉIG kell érnie, a mintának viszont épp a
   * paddingen belül kell maradnia.
   *
   * A felirat a szótárból megy a dokumentumba, nem a `badgePrint.ts`-ből: a
   * config-modul nem ismeri a felület nyelvét, és nem is kell ismernie.
   */
  const handleCalibrationLabel = async () => {
    try {
      await printer.printCalibration({
        title: t('checkin.calibrationTitle'),
        hint: t('checkin.calibrationHint'),
      });
    } catch (error) {
      if (error instanceof Error) console.error('Calibration label printing failed:', error);
      // SAJÁT üzenet, nem a névtábláé: a kezelő a „Kalibrációs címke" gombot
      // nyomta meg, és egy „a névtábla nyomtatása nem indult el" sor arra
      // terelné, hogy rossz gombot nyomott.
      message.error(printErrorText(error, 'checkin.calibrationPrintFailed'));
    }
  };

  const handleUndoCheckIn = async (ticketId: string) => {
    writesInFlight.current += 1;
    try {
      await api.post(`/tickets/${ticketId}/undo-check-in`);
      message.success(t('checkin.undoSuccess'));
      fetchTickets(selectedEvent);
      fetchStatistics(selectedEvent);
    } catch (error: any) {
      message.error(error.response?.data?.message || t('checkin.undoFailed'));
    } finally {
      writesInFlight.current -= 1;
    }
  };

  const handleBulkCheckIn = async () => {
    if (selectedRows.length === 0) {
      message.warning(t('checkin.selectAtLeastOne'));
      return;
    }

    writesInFlight.current += 1;
    try {
      const promises = selectedRows.map((ticketId) => api.post(`/tickets/${ticketId}/check-in`));

      await Promise.all(promises);
      message.success(t('checkin.bulkSuccess', { count: selectedRows.length }));
      setSelectedRows([]);
      fetchTickets(selectedEvent);
      fetchStatistics(selectedEvent);
    } catch (error) {
      message.error(t('checkin.bulkFailed'));
    } finally {
      writesInFlight.current -= 1;
    }
  };

  // No spinner, no toast, and a failure keeps what is on screen: the next cycle retries.
  const refreshTickets = async (eventId: string) => {
    const seq = ++ticketsSeq.current;
    try {
      const result = await fetchAllTickets<Ticket>(
        { eventId },
        {
          isStale: () => eventId !== liveEventRef.current,
          silentError: true,
          timeout: REFRESH_TIMEOUT_MS,
        }
      );
      if (!result || seq !== ticketsSeq.current) return;
      setTickets(result.data);
      // Lifts a failed first load, so a scanned code is looked up again.
      setListStatus({ eventId, ok: true });
    } catch {}
  };

  const refreshStatistics = async (eventId: string) => {
    const seq = ++statsSeq.current;
    try {
      const response = await api.get(`/tickets/statistics/${eventId}`, {
        silentError: true,
        timeout: REFRESH_TIMEOUT_MS,
      } as never);
      if (eventId === liveEventRef.current && seq === statsSeq.current) {
        setStatistics(response.data);
      }
    } catch {}
  };

  /**
   * A cycle is skipped while an operator is in the middle of something the new
   * list could replace under them: an open window, a print, a check-in on its
   * way, or a visible load already running.
   */
  const refreshQuietly = () => {
    const eventId = liveEventRef.current;
    const busy =
      loading ||
      statsLoading ||
      scanned !== null ||
      newGuestOpen ||
      newGuestSaving !== null ||
      printingId !== null ||
      scanBusy !== null ||
      writesInFlight.current > 0 ||
      refreshInFlight.current;
    if (!eventId || busy) return;
    refreshInFlight.current = true;
    Promise.all([refreshTickets(eventId), refreshStatistics(eventId)]).finally(() => {
      refreshInFlight.current = false;
    });
  };

  useSilentRefresh(refreshQuietly, CHECKIN_REFRESH_MS, Boolean(selectedEvent));

  /*
    A hasonló-név kereséshez használt NÉVLISTA, egyszer származtatva.

    A modális minden leütésre újra lefuttatja a keresést, az pedig minden
    jelölt nevét normalizálja (NFD + két reguláris kifejezés). Ezer résztvevőre
    — ez a tervezési célszám — ez leütésenként ezer normalizálás, pont abban a
    mezőben, aminek nem szabad akadnia. A `useMemo` a listához köti, nem a
    gépeléshez.
  */
  const similarCandidates = useMemo(
    () =>
      tickets.map((ticket) => ({
        key: ticket.id,
        name: attendeeName(ticket),
        ticketNumber: ticket.ticketNumber,
        checkedIn: ticket.checkedIn,
      })),
    [tickets]
  );

  const filteredTickets = tickets.filter((ticket) => {
    const matchesCheckIn = filterCheckedIn === undefined || ticket.checkedIn === filterCheckedIn;
    // Karlo terve — kategória-szűrő. A `PARTICIPANT` az adatbázis
    // alapértelmezése, tehát a mező hiánya NEM „nincs kategóriája", hanem egy
    // olyan válasz, amiben nem jött meg. Ilyenkor a szűrő ne rejtse el a sort
    // némán: a pultnál egy eltűnt résztvevő rosszabb, mint egy rossz csoportba
    // sorolt.
    const matchesType =
      typeFilter === undefined ||
      ticket.attendeeType === undefined ||
      ticket.attendeeType === typeFilter;

    const matchesSearch = matchesTicketSearch(ticket, searchText);
    return matchesCheckIn && matchesType && matchesSearch;
  });

  const columns: ColumnsType<Ticket> = [
    {
      title: '',
      key: 'select',
      width: 50,
      render: (_, record) => (
        <Checkbox
          checked={selectedRows.includes(record.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, record.id]);
            } else {
              setSelectedRows(selectedRows.filter((id) => id !== record.id));
            }
          }}
          disabled={record.checkedIn}
        />
      ),
    },
    {
      title: t('checkin.columnTicketNumber'),
      dataIndex: 'ticketNumber',
      key: 'ticketNumber',
      render: (text) => (
        <Space>
          <QrcodeOutlined />
          <Text copyable>{text}</Text>
        </Space>
      ),
    },
    {
      title: t('checkin.columnAttendee'),
      key: 'user',
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <Space direction="vertical" size={0}>
            <Text strong>{attendeeName(record) || t('checkin.guest')}</Text>
            <Space>
              <MailOutlined style={{ fontSize: 12 }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.email || '-'}
              </Text>
            </Space>
            {record.phone && (
              <Space>
                <PhoneOutlined style={{ fontSize: 12 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {record.phone}
                </Text>
              </Space>
            )}
          </Space>
        </Space>
      ),
    },
    {
      // #168 — a pultnál a cég a keresés fő fogódzója, és ez kerül a névtábla
      // felső sorára. Az oszlop és a névtábla UGYANABBÓL a függvényből ír,
      // hogy a képernyő és a kinyomtatott címke ne mondhasson mást.
      title: t('checkin.columnCompany'),
      key: 'company',
      render: (_, record) => {
        const company = badgeCompanyName(record);
        return company ? (
          <Text>{company}</Text>
        ) : (
          <Text type="secondary">{t('checkin.noCompany')}</Text>
        );
      },
    },
    {
      // Karlo terve — külön Cég ÉS Típus oszlop. A típus az, ami alapján az
      // ajtóban álló eldönti, hova engedheti be a résztvevőt, és ez kerül a
      // névtábla harmadik sorára is.
      title: t('checkin.columnType'),
      key: 'attendeeType',
      render: (_, record) => {
        const label = attendeeTypeLabel(record);
        // Ismeretlen vagy hiányzó kategórián nem írunk ki semmit. A nyers
        // enum-érték („MEDIA_PARTNER") a kezelőnek nem mond többet a
        // kihagyott cellánál, a szótárból hiányzó kulcs viszont a kulcs
        // nevét írná ki a táblázatba.
        return label ? <Tag>{label}</Tag> : <Text type="secondary">—</Text>;
      },
    },
    {
      title: t('checkin.columnTicketType'),
      key: 'ticketType',
      // Tag is nowrap by default: one long type name would widen this column
      // and crush the attendee column under the table's auto layout.
      render: (_, record) => (
        <Tag color="blue" style={{ whiteSpace: 'normal', maxWidth: 180 }}>
          {record.TicketType?.name || 'N/A'}
        </Tag>
      ),
    },
    {
      title: t('checkin.columnPurchasedAt'),
      dataIndex: 'purchasedAt',
      key: 'purchasedAt',
      render: (date) => new Date(date).toLocaleString(localeTag(i18n.language)),
    },
    {
      title: t('common.status'),
      key: 'status',
      filters: [
        { text: t('checkin.checkedIn'), value: true },
        { text: t('checkin.notCheckedIn'), value: false },
      ],
      onFilter: (value, record) => record.checkedIn === value,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.checkedIn ? (
            <>
              <Tag icon={<CheckCircleOutlined />} color="success">
                {t('checkin.checkedIn')}
              </Tag>
              {record.checkedInAt && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {new Date(record.checkedInAt).toLocaleString(localeTag(i18n.language))}
                </Text>
              )}
            </>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="default">
              {t('checkin.notCheckedIn')}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      // Only opens the correction window; the Action button stays the
      // one-click print path.
      title: '',
      key: 'edit',
      width: 56,
      render: (_, record) => (
        <Tooltip title={t(badgePrinting ? 'checkin.editBadge' : 'checkin.editDetails')}>
          <Button
            icon={<EditOutlined />}
            aria-label={t(badgePrinting ? 'checkin.editBadge' : 'checkin.editDetails')}
            onClick={() => setScanned({ kind: 'ticket', ticket: record, via: 'row' })}
          />
        </Tooltip>
      ),
    },
    {
      title: t('checkin.columnAction'),
      key: 'action',
      fixed: 'right',
      render: (_, record) =>
        // Karlo terve 2026-09-16 — a soron ÁLLAPOTTÓL FÜGGŐEN egy művelet áll,
        // nem kettő. Nem bejelentkezett soron „Nyomtatás + beléptetés", mert a
        // pultnál a kettő egyetlen mozdulat: a kezelő nem azt akarja eldönteni,
        // hogy érkeztet-e VAGY nyomtat, hanem hogy beengedi-e a sorban állót.
        // Bejelentkezett soron „Újranyomtatás" + visszavonás — az elakadt
        // címkét ki kell tudni adni anélkül, hogy előbb vissza kelljen vonni
        // az érkezést.
        //
        // A gomb MINDKÉT állapotban ugyanazt a `handleRowPrint`-et (azon át a
        // `handlePrintBadge`-et) hívja: a különbség a feliratban van, nem a
        // műveletben. A már bent lévő soron
        // az érkeztetést a függvény két őre fogja meg (a soron álló `checkedIn`
        // és a szerver „already checked in" válasza).
        //
        // #202 — nyomtató nélküli eseményen a gomb „Beléptetés", a bent lévő
        // soron pedig csak a visszavonás marad: nincs mit újranyomtatni.
        !record.checkedIn ? (
          <Button
            type="primary"
            icon={badgePrinting ? <PrinterOutlined /> : <LoginOutlined />}
            // Spins until the desk knows whether it prints (#202, `deskKnown`).
            loading={printingId === record.id || !deskKnown}
            onClick={() => handleRowPrint(record)}
          >
            {t(badgePrinting ? 'checkin.printAndCheckIn' : 'checkin.checkInOnly')}
          </Button>
        ) : (
          <Space>
            {badgePrinting && (
              <Button
                icon={<PrinterOutlined />}
                loading={printingId === record.id}
                onClick={() => handleRowPrint(record)}
              >
                {t('checkin.reprintButton')}
              </Button>
            )}
            <Tooltip title={t('checkin.undoButton')}>
              <Button
                type="default"
                icon={<UndoOutlined />}
                aria-label={t('checkin.undoButton')}
                onClick={() => handleUndoCheckIn(record.id)}
                danger
              />
            </Tooltip>
          </Space>
        ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" gutter={[16, 12]} style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <TeamOutlined /> {t('checkin.title')}
          </Title>
        </Col>
        {/*
          A fejléc eszközsora TÖRDELŐDIK (#178): a nyomtatóválasztóval
          szélesebb lett, és tördelés nélkül kitolta az oldalt — a táblázat
          oszlopai ettől betűnyire szűkültek.
        */}
        <Col flex="1 1 auto" style={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
          <Space wrap style={{ justifyContent: 'flex-end' }}>
            {/*
              Karlo terve 2026-09-16 — két állapotjelző az eseményválasztó
              mellett. A NYOMTATÓÉ a #178 óta valódi állapot, ha a pult a
              Zebrára nyomtat (print agent); böngészős tartalékban semleges —
              lásd a `PrinterIndicator` indoklását.
            */}
            {/* #202 — a nyomtató nélküli esemény pultján nincs mit jelezni. */}
            {badgePrinting && (
              <>
                <PrinterIndicator
                  mode={printer.mode}
                  state={printer.state}
                  queued={printer.agent?.queued ?? 0}
                  agents={printer.agents}
                  selectedId={printer.agent?.id ?? null}
                  onSelect={printer.select}
                  onTestLabel={handleTestLabel}
                  onCalibrationLabel={handleCalibrationLabel}
                  onSetup={() => setPrintSetupOpen(true)}
                  setupDisabled={!selectedEvent}
                />
                <PrintAgentsModal
                  open={printSetupOpen}
                  eventId={selectedEvent}
                  onClose={() => setPrintSetupOpen(false)}
                  onChanged={printer.refresh}
                />
              </>
            )}
            {/*
              A QR olvasóé viszont VALÓDI állapot, és ezért zöld vagy szürke:
              az USB-s olvasó billentyűzetként gépel, tehát a beolvasás oda fut
              be, ahol a fókusz van. Fókusz nélkül a beolvasás némán elvész —
              ezt a jelzőt nézve a kezelő ezt előre látja.
            */}
            <Tag
              color={searchFocused ? 'green' : undefined}
              style={{ padding: '4px 10px', margin: 0 }}
            >
              <Space size={6}>
                <QrcodeOutlined />
                {t('checkin.scannerIndicator')}
                <Text type={searchFocused ? 'success' : 'secondary'}>
                  {searchFocused ? t('checkin.scannerReady') : t('checkin.scannerIdle')}
                </Text>
              </Space>
            </Tag>
            <Select
              style={{ width: 300 }}
              placeholder={t('checkin.selectEvent')}
              value={selectedEvent}
              onChange={setSelectedEvent}
              showSearch
              optionFilterProp="children"
            >
              {events.map((event) => (
                <Option key={event.id} value={event.id}>
                  {event.title}
                </Option>
              ))}
            </Select>
          </Space>
        </Col>
      </Row>

      {/*
        Three states here, not two. With numbers, the cards show them; while
        they are on their way and there are none yet, the cards hold their place
        as skeletons — otherwise every event switch would drop the table by the
        height of this row, under the finger of whoever is tapping through a
        queue. Only a FAILED fetch takes the row away entirely, which is the
        whole point of this screen's fix: it may show this event's numbers or
        none, never another event's.
      */}
      {selectedEvent && (statistics || statsLoading) && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card loading={!statistics}>
              <Statistic
                title={t('checkin.statTotal')}
                value={statistics?.total}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card loading={!statistics}>
              <Statistic
                title={t('checkin.checkedIn')}
                value={statistics?.checkedIn}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card loading={!statistics}>
              <Statistic
                title={t('checkin.notCheckedIn')}
                value={statistics?.notCheckedIn}
                valueStyle={{ color: '#cf1322' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card loading={!statistics}>
              <Statistic
                title={t('checkin.statCheckInRate')}
                value={formatPercent(statistics?.checkInRate, i18n.language)}
                valueStyle={{ color: (statistics?.checkInRate ?? 0) >= 50 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Row justify="space-between">
            <Col>
              <Space>
                <Search
                  ref={searchRef}
                  placeholder={t('checkin.searchPlaceholder')}
                  value={searchText}
                  // Enter = beolvasás VAGY keresés, a beütött szövegtől
                  // függően. Az antd `Search` ugyanezt hívja a nagyító
                  // gombra is, ami itt helyes: a kezelő kézzel beütött
                  // jegyszámára ugyanúgy nyíljon az ablak.
                  onSearch={handleSearchEnter}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  style={{ width: 400 }}
                  prefix={<SearchOutlined />}
                  allowClear
                />
                {/* Karlo terve — kategória-szűrő a kereső mellett. */}
                <Select
                  style={{ width: 180 }}
                  placeholder={t('checkin.filterAllTypes')}
                  value={typeFilter}
                  onChange={setTypeFilter}
                  allowClear
                >
                  {ATTENDEE_TYPES.map((value) => (
                    <Option key={value} value={value}>
                      {/*
                        Közvetlenül a térképből, nem az `attendeeTypeLabelKey`
                        futásidejű keresésén át: ezek a mi saját értékeink, nem
                        az API-é, tehát a kulcs itt nem lehet `null`, és a
                        fordító ezt látja is.
                      */}
                      {t(ATTENDEE_TYPE_LABEL_KEYS[value])}
                    </Option>
                  ))}
                </Select>
                <Space.Compact>
                  <Button
                    type={filterCheckedIn === undefined ? 'primary' : 'default'}
                    onClick={() => setFilterCheckedIn(undefined)}
                  >
                    {t('checkin.filterAll')}
                  </Button>
                  <Button
                    type={filterCheckedIn === false ? 'primary' : 'default'}
                    onClick={() => setFilterCheckedIn(false)}
                  >
                    {t('checkin.filterWaiting')}
                  </Button>
                  <Button
                    type={filterCheckedIn === true ? 'primary' : 'default'}
                    onClick={() => setFilterCheckedIn(true)}
                  >
                    {t('checkin.filterCheckedIn')}
                  </Button>
                </Space.Compact>
              </Space>
            </Col>
            <Col>
              <Space>
                {selectedRows.length > 0 && (
                  <Button type="primary" icon={<CheckOutlined />} onClick={handleBulkCheckIn}>
                    {t('checkin.bulkButton', { n: selectedRows.length })}
                  </Button>
                )}
                {/*
                  Karlo terve — „Új vendég" a szűrősor jobb szélén, sötét
                  gombként (#178, a design szerint). A fejlécből azért került
                  ide, mert ott a nyomtatóválasztóval együtt nem fért ki, és a
                  kézi pult kezelője a kereső mellett keresi: akit nem talál a
                  listán, azt innen viszi fel.

                  Esemény nélkül nincs mihez jegyet kiadni — de a gomb LÁTSZIK,
                  csak nem nyomható: így a kezelő tudja, hogy a pultról
                  egyáltalán lehet vendéget felvinni.
                */}
                <Button
                  color="default"
                  variant="solid"
                  icon={<UserAddOutlined />}
                  disabled={!selectedEvent}
                  onClick={() => setNewGuestOpen(true)}
                >
                  {t('checkin.newGuest')}
                </Button>
              </Space>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={filteredTickets}
            rowKey="id"
            loading={loading}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => t('checkin.totalTickets', { count: total }),
            }}
            locale={{
              emptyText: selectedEvent
                ? t('checkin.emptyNoTickets')
                : t('checkin.emptySelectEvent'),
            }}
          />

          {/*
            Karlo terve — a lista alján álló tipp. Nem dekoráció: a beolvasásnak
            nincs saját gombja a képernyőn (nem is lehet, az olvasó gépel), ezért
            ez az EGYETLEN hely, ahonnan a kezelő megtudja, hogy egyáltalán
            beolvashat.
          */}
          {selectedEvent && (
            <Text type="secondary">
              <QrcodeOutlined /> {t('checkin.scanHint')}
            </Text>
          )}
        </Space>
      </Card>

      <ScanResultModal
        state={scanned}
        badge={badgeContent}
        original={ticketOriginal}
        typeLabel={attendeeTypeLabel}
        formatTime={(iso) => new Date(iso).toLocaleString(localeTag(i18n.language))}
        // Until the desk knows whether it prints, the primary button spins and Enter waits.
        busy={scanBusy ?? (deskKnown ? null : 'print')}
        printEnabled={badgePrinting}
        onClose={closeScan}
        onPrint={handleScanPrint}
        onSave={handleScanSave}
        onScan={handleWindowScan}
        codeStart={(typed) => codeStartIn(typed, selectedEvent)}
      />

      <NewGuestModal
        open={newGuestOpen}
        ticketTypes={ticketTypes ?? []}
        // `null` = a jegytípusok BETÖLTÉSE hasalt el, nem az, hogy nincs
        // jegytípus. A kettőre más a teendő.
        ticketTypesFailed={ticketTypes === null}
        onRetryTicketTypes={() => fetchTicketTypes(selectedEvent)}
        badge={newGuestBadge}
        printEnabled={badgePrinting}
        findSimilar={(draft) => newGuestSimilar(draft, similarCandidates)}
        // A duplikáció-ellenőrzés csak akkor mond valamit, ha VAN mihez
        // hasonlítani. Betöltés közben a lista üres, és egy néma „nincs
        // egyezés" megkülönböztethetetlen a valódi nemleges választól —
        // pedig a sárga doboz egész értéke az, hogy a HIÁNYA is jelent
        // valamit.
        listReady={listReady}
        saving={newGuestSaving}
        onCancel={closeNewGuest}
        onSubmit={handleNewGuest}
      />
    </div>
  );
};

export default CheckIn;
